import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import 'assets/vuetify-extend.scss'

import { createVuetify } from 'vuetify'
import type {
  DateInstance,
  DefaultsInstance,
  DisplayInstance,
  IconOptions,
  LocaleInstance,
  RtlInstance,
  ThemeDefinition,
  ThemeInstance,
} from 'vuetify'

import CheckAutomatic from 'assets/icons/CheckAutomatic.svg'
import CheckManual from '~/assets/icons/CheckManual.svg'
import EditDate from '~/assets/icons/EditDate.svg'
import FinalizeAll from '~/assets/icons/FinalizeAll.svg'
import GanttChart from '~/assets/icons/GanttChart.svg'
import GridOn from '~/assets/icons/GridOn.svg'
import NetDiagram from '~/assets/icons/NetDiagram.svg'
import NetDiagramColoring from '~/assets/icons/NetDiagramColoring.svg'
import NetDiagramOnly from '~/assets/icons/NetDiagramOnly.svg'
import LightingBoltCity from '~/assets/icons/LightingBoltCity.svg'
import LightingBoltHome from '~/assets/icons/LightingBoltHome.svg'
import TransmissionTowerBolt from '~/assets/icons/TransmissionTowerBolt.svg'

import type { useI18n as useI18nComposable, I18n } from 'vue-i18n'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'
import { markRaw } from 'vue'
import type { UnwrapNestedRefs } from 'vue'
import { VBtn } from 'vuetify/components/VBtn'
import { VFadeTransition, VSwitch } from 'vuetify/components'
import { VDateInput, VIconBtn, VHotkey } from 'vuetify/labs/components'
import type { ComponentProps } from 'vue-component-type-helpers'

declare module '~/types/vue-shim' {
  interface AppGlobalProperties {
    /**
     * Copy of Interface from vuetify module
     */
    $vuetify: {
      defaults: DefaultsInstance
      display: UnwrapNestedRefs<DisplayInstance>
      theme: UnwrapNestedRefs<ThemeInstance>
      icons: IconOptions
      locale: UnwrapNestedRefs<LocaleInstance & RtlInstance>
      date: DateInstance
    }
  }
}

export default function createVuetifyPlugin(i18n: I18n, useI18n: typeof useI18nComposable) {
  return createVuetify({
    locale: {
      // vue-i18n integration
      // See: https://vuetifyjs.com/en/features/internationalization/#vue-i18n
      adapter: createVueI18nAdapter({ i18n, useI18n }),
    },
    components: {
      VIconBtn,
      VDateInput,
      VHotkey,
    },
    aliases: Aliases,
    // https://vuetifyjs.com/en/features/global-configuration/
    defaults: {
      ...aliasesDefaults,
      // https://vuetifyjs.com/en/api/v-btn/
      VBtn: {
        flat: 'true',
      },
      VIconBtn: {
        flat: 'true',
      },
      // https://vuetifyjs.com/en/api/v-slider/
      VSlider: {
        density: 'compact',
        color: 'primary',
      },
      // https://vuetifyjs.com/en/api/v-tooltip/
      VTooltip: {
        eager: false,
        interactive: true,
        transition: { component: markRaw(VFadeTransition) },
        location: 'top',
        closeDelay: '150',
        openDelay: '300', // 300-500 is generally accepted
        contentClass: 'elevation-5 text-caption',
      },
      // https://vuetifyjs.com/en/api/v-combobox/
      VCombobox: {
        variant: 'underlined',
      },
      // https://vuetifyjs.com/en/api/v-text-field/
      VTextField: {
        variant: 'underlined',
      },
      // https://vuetifyjs.com/en/api/v-select/
      VSelect: {
        variant: 'underlined',
      },
      VNumberInput: {
        variant: 'underlined',
      },
      // https://vuetifyjs.com/en/api/v-card-title/
      VCardTitle: {
        class: 'd-flex align-top',
      },
      // https://vuetifyjs.com/en/api/v-date-picker/
      VDatePicker: {
        firstDayOfWeek: 1,
        showWeek: true,
        showAdjacentMonths: true,
      },
      // https://vuetifyjs.com/en/api/v-list/
      VList: {
        class: 'px-2',
        density: 'compact',
      },
      // https://vuetifyjs.com/en/api/v-list-item/
      VListItem: {
        rounded: true,
      },
      VAppBar: {
        elevation: '0',
        color: 'primary',
        class: 'px-4',
        height: '48',
      },
      VContainer: {
        maxWidth: 1100,
        fluid: true,
      },
    },
    icons: {
      defaultSet: 'mdi',
      /**
       * use as
       * <v-icon>
       *  $[icon-name]
       * </v-icon>
       */
      aliases: {
        EditDate,
        CheckManual,
        CheckAutomatic,
        FinalizeAll,
        GanttChart,
        GridOn,
        NetDiagram,
        NetDiagramOnly,
        NetDiagramColoring,
        LightingBoltCity,
        LightingBoltHome,
        TransmissionTowerBolt,
      },
    },
    theme: {
      defaultTheme: 'light',
      /**
       * Only defining dark and light theme.
       * For component/text/background color configuration see: [vuetify-settings.scss]{@link ./assets/scss/vuetify-variables.scss}
       */
      themes: {
        light: LightTheme,
        dark: DarkTheme,
      },
      variations: {
        colors: ['primary', 'secondary', 'success', 'warning', 'error', 'surface', 'background'],
        lighten: 4,
        darken: 4,
      },
    },
  })
}

/**
 * Commented are defaults
 */
const CommonThemeVariables: Record<string, string | number> = {
  // 'border-color': '#000000',
  // 'border-opacity': 0.12,
  // 'high-emphasis-opacity': 0.87,
  // 'medium-emphasis-opacity': 0.6,
  // 'disabled-opacity': 0.38,
  // 'idle-opacity': 0.04,
  // 'hover-opacity': 0.04,
  'hover-opacity': 0.3,
  // 'focus-opacity': 0.12,
  // 'selected-opacity': 0.08,
  'activated-opacity': 0.2,
  // 'pressed-opacity': 0.12,
  // 'dragged-opacity': 0.08,
  // 'theme-kbd': '#212529',
  // 'theme-on-kbd': '#FFFFFF',
  // 'theme-code': '#F5F5F5',
  // 'theme-on-code': '#000000',
}

/**
 * Transnet Corporate Design Colors
 *
 * Among other things, used for PDF export
 */
const TransnetColors = {
  transnetPetrol: '#004754',
  transnetOlive: '#9A9A2E',
  transnetDark: '#000000',
  transnetGrey: '#7E7E7E',
}

const LightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    /**
     * Native
     */
    background: '#fff', // background-color for v-app, v-calendar (v-card, v-table)
    'on-background': '#2C3E50',

    surface: '#ECF0F1', // background-color for most other components
    'on-surface': '#2C3E50',

    primary: '#2ECC71',
    'on-primary': '#2C3E50',

    secondary: '#2C3E50',
    'on-secondary': '#ECF0F1',

    success: '#4caf50',
    warning: '#fb8c00',
    error: '#e74c3c',

    /**
     * Custom
     */
    accent: '#2ECC71',
    border: '#c2cfd2', // darkened by 15% of background

    ...TransnetColors,
  },
  variables: CommonThemeVariables,
}

/**
 * Values mainly darkened by 20%
 * See: https://pinetools.com/darken-color
 */
const DarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    /**
     * Native
     */
    background: '#354b61',
    'on-background': '#ECF0F1',

    surface: '#2C3E50',
    'on-surface': '#ECF0F1',

    primary: '#24a35a',
    'on-primary': '#ECF0F1',

    secondary: '#ECF0F1',
    'on-secondary': '#24a35a',

    success: '#4caf50',
    warning: '#fb8c00',
    error: '#e74c3c',

    /**
     * Custom
     */
    accent: '#24a35a',
    border: '#405a75', // lightened by 15% of background

    ...TransnetColors,
  },
  variables: CommonThemeVariables,
}

declare module '@vue/runtime-core' {
  interface GlobalComponents {
    VBtnIcon: typeof VBtn
    VSwitchSimple: typeof VSwitch
  }
}
const Aliases = {
  VBtnIcon: VBtn,
  VSwitchSimple: VSwitch,
}

type AliasesDefaults = {
  [K in keyof typeof Aliases]: Partial<ComponentProps<(typeof Aliases)[K]>>
}

const aliasesDefaults: AliasesDefaults = {
  VBtnIcon: {
    variant: 'text',
    icon: true,
    flat: true,
    density: 'comfortable',
  },
  VSwitchSimple: {
    density: 'compact',
    hideDetails: true,
    inset: true,
    color: 'primary',
  },
}
